import JobClinics from '@/components/admin/JobClinics';
import { useEffect, useState } from 'react';
import { useCan, useMyScopes } from '@/hooks/use-scopes';
import { useNavigate, useParams, Navigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, Mail, Save, ShieldCheck, ShieldOff, Activity as ActivityIcon,
  AlertTriangle, Check, RefreshCw, Copy,
} from 'lucide-react';
import { toast } from 'sonner';
import { Card, PageHeader } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import NumberInput from '@/components/ui/NumberInput';
import { api } from '@/lib/api';
import { fetchUserCapacity } from '@/lib/queries';
import { DESIGNATIONS, ROLES, type Role, type Designation } from '@shared/enums';
import { cn } from '@/lib/cn';

interface UserRow {
  id: string;
  email: string;
  display_name: string;
  photo_url?: string | null;
  role: Role;
  designations: Designation[];
  active: boolean;
  weekly_capacity: number;
  last_seen_at?: string | null;
  invited_at?: string | null;
  invite_expires_at?: string | null;
  notification_prefs?: { email: boolean; telegram: boolean; push: boolean; daily_digest: boolean };
  created_at: string;
}

interface InviteResponse {
  user: UserRow; invite_token: string; invite_url: string; expires_in_days: number;
}

export default function UserDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const allowed = useCan('admin.panel');
  // a direct link or a refresh must wait for the scopes, or an owner is bounced home
  const scopesLoading = useMyScopes().isLoading;

  const user = useQuery({
    queryKey: ['user', id],
    queryFn: async () => {
      const all = (await api.get<UserRow[]>('/users')).data;
      return all.find((u) => u.id === id) ?? null;
    },
    enabled: !!id,
  });
  const cap = useQuery({
    queryKey: ['user-capacity', id],
    queryFn: () => fetchUserCapacity(id),
    enabled: !!id,
  });

  // Editable form state
  const [name, setName] = useState('');
  const [role, setRole] = useState<Role>('crew');
  const [designations, setDesignations] = useState<Designation[]>([]);
  const [capacity, setCapacity] = useState<number>(0);
  const [active, setActive] = useState(true);
  const [emailEnabled, setEmailEnabled] = useState(true);
  const [telegramEnabled, setTelegramEnabled] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(true);
  const [digestEnabled, setDigestEnabled] = useState(true);
  const [hydrated, setHydrated] = useState(false);
  const [invite, setInvite] = useState<InviteResponse | null>(null);

  // Hydrate form from server once user loads
  useEffect(() => {
    if (user.data && !hydrated) {
      setName(user.data.display_name);
      setRole(user.data.role);
      setDesignations(user.data.designations);
      setCapacity(user.data.weekly_capacity ?? 0);
      setActive(user.data.active);
      const np = user.data.notification_prefs ?? { email: true, telegram: false, push: true, daily_digest: true };
      setEmailEnabled(np.email);
      setTelegramEnabled(np.telegram);
      setPushEnabled(np.push);
      setDigestEnabled(np.daily_digest);
      setHydrated(true);
    }
  }, [user.data, hydrated]);

  const save = useMutation({
    mutationFn: async () => (await api.patch<UserRow>(`/users/${id}`, {
      display_name: name.trim() || undefined,
      role,
      designations,
      weekly_capacity: capacity,
      active,
      notification_prefs: {
        email: emailEnabled,
        telegram: telegramEnabled,
        push: pushEnabled,
        daily_digest: digestEnabled,
      },
    })).data,
    onSuccess: () => {
      toast.success('Saved');
      qc.invalidateQueries({ queryKey: ['user', id] });
      qc.invalidateQueries({ queryKey: ['user-capacity', id] });
      qc.invalidateQueries({ queryKey: ['users'] });
      qc.invalidateQueries({ queryKey: ['team-capacity'] });
    },
  });

  const reissue = useMutation({
    mutationFn: async () => (await api.post<InviteResponse>(`/users/${id}/reissue-invite`)).data,
    onSuccess: (data) => {
      toast.success('New invite link generated');
      setInvite(data);
      qc.invalidateQueries({ queryKey: ['user', id] });
      qc.invalidateQueries({ queryKey: ['users'] });
    },
  });

  if (scopesLoading) return <div className="text-muted text-sm">Loading...</div>;
  if (!allowed) return <Navigate to="/" replace />;
  if (user.isLoading) return <div className="text-muted text-sm">Loading...</div>;
  if (!user.data) return <Navigate to="/admin/users" replace />;
  const u = user.data;
  const c = cap.data;

  const util = c?.utilization_pct ?? 0;
  const utilColor = util > 100 ? 'text-red-400' : util > 80 ? 'text-yellow-400' : 'text-emerald-400';
  const barColor  = util > 100 ? 'bg-red-500/70' : util > 80 ? 'bg-yellow-500/70' : 'bg-emerald-500/60';

  // One approval job per account (Aziz 2026-09-29): picking one job drops the other.
  const JOBS: Designation[] = ['art_director', 'content_creator'] as Designation[];
  function toggleDesignation(d: Designation) {
    setDesignations((cur) => {
      if (cur.includes(d)) return cur.filter((x) => x !== d);
      const rest = JOBS.includes(d) ? cur.filter((x) => !JOBS.includes(x)) : cur;
      return [...rest, d];
    });
  }
  const savedJob = (u.designations ?? []).find((x) => JOBS.includes(x));
  const draftJob = designations.find((x) => JOBS.includes(x));
  async function copyInvite() {
    if (!invite) return;
    try {
      await navigator.clipboard.writeText(invite.invite_url);
      toast.success('Invite link copied');
    } catch { toast.error('Copy failed'); }
  }

  return (
    <div>
      <Link to="/admin/users" className="inline-flex items-center gap-1 text-xs text-muted hover:text-accent-400 mb-3">
        <ArrowLeft size={12} /> Back to users
      </Link>

      <PageHeader
        title={u.display_name}
        subtitle={u.email}
        actions={
          <Button onClick={() => save.mutate()} loading={save.isPending} icon={<Save size={14} />}>Save changes</Button>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Editable form (2 cols) */}
        <div className="lg:col-span-2 space-y-5">
          <Card className="space-y-5">
            <h3 className="font-medium text-muted-strong text-sm">Profile</h3>
            <Input label="Display name" value={name} onChange={(e) => setName(e.target.value)} />
            <Input label="Email (sign-in identifier)" value={u.email} readOnly hint="Cannot be changed - reach out to support" />
            <Select label="Role" value={role} onChange={(v) => setRole(v as Role)}
              options={ROLES.map((r) => ({ value: r, label: r }))} />
          </Card>

          <Card className="space-y-3">
            <h3 className="font-medium text-muted-strong text-sm">
              Designations <span className="text-muted text-xs">({designations.length} selected)</span>
            </h3>
            <p className="text-xs text-muted">
              Drives auto-routing. A person can be both Videographer and Photographer, but only one of
              Art director or Content creator: that is their job, and it decides what they approve.
            </p>
            <div className="flex flex-wrap gap-1.5">
              {DESIGNATIONS.map((d) => (
                <button key={d} type="button" onClick={() => toggleDesignation(d)}
                  className={cn(
                    'text-[11px] px-2.5 py-1 rounded-full border transition',
                    designations.includes(d)
                      ? 'border-accent-400 bg-accent-600/20 text-accent-400'
                      : 'border-slate-800 text-muted hover:text-muted-strong'
                  )}>
                  {d.replace(/_/g, ' ')}
                </button>
              ))}
            </div>
          </Card>

          <JobClinics userId={u.id} jobDirty={savedJob !== draftJob} />

          <Card className="space-y-3">
            <h3 className="font-medium text-muted-strong text-sm">Capacity</h3>
            <p className="text-xs text-muted">
              Concurrent open tasks this person can hold. Used by the team capacity dashboard
              and (Phase 5) the auto-routing engine to pick the least-loaded match.
            </p>
            <div className="max-w-xs">
              <NumberInput value={capacity} onChange={setCapacity} min={0} suffix="tasks" />
            </div>
          </Card>

          <Card className="space-y-3">
            <h3 className="font-medium text-muted-strong text-sm">Notification preferences</h3>
            <Toggle label="Email - instant on assignment + daily digest" checked={emailEnabled} onChange={setEmailEnabled} />
            <Toggle label="Browser push (PWA)" checked={pushEnabled} onChange={setPushEnabled} />
            <Toggle label="Telegram bot" checked={telegramEnabled} onChange={setTelegramEnabled} hint="Phase 5 - bot link required" />
            <Toggle label="Daily digest at 8am Asia/Dubai" checked={digestEnabled} onChange={setDigestEnabled} />
          </Card>

          <Card className="space-y-3">
            <h3 className="font-medium text-muted-strong text-sm">Account state</h3>
            <Toggle
              label={active ? 'Active - can sign in and be assigned tasks' : 'Inactive - cannot sign in'}
              checked={active} onChange={setActive}
            />
          </Card>
        </div>

        {/* Sidebar: capacity stats + invite tools */}
        <div className="space-y-5">
          <Card>
            <div className="flex items-center justify-center mb-4">
              {u.photo_url ? (
                <img src={u.photo_url} alt="" className="w-20 h-20 rounded-full object-cover" />
              ) : (
                <div className="w-20 h-20 rounded-full bg-accent-600/20 text-accent-400 flex items-center justify-center text-2xl font-semibold">
                  {u.display_name.slice(0, 2).toUpperCase()}
                </div>
              )}
            </div>
            <div className="text-center mb-4">
              <span className={cn('inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wide',
                u.active ? 'bg-emerald-600/15 text-emerald-300' : 'bg-slate-700/40 text-slate-400'
              )}>
                {u.role}{u.active ? '' : ' · inactive'}
              </span>
            </div>
            {c && (
              <>
                <div className="flex items-baseline justify-between text-xs mb-1.5">
                  <div className="text-muted">Utilization</div>
                  <div className={cn('font-mono', utilColor)}>
                    {c.weekly_capacity > 0 ? `${util}%` : 'no cap'}
                  </div>
                </div>
                <div className="h-2 rounded-full bg-slate-800 overflow-hidden mb-3">
                  <div className={cn('h-full', barColor)} style={{ width: `${Math.min(util, 100)}%` }} />
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <Stat label="Open" value={c.open_tasks} />
                  <Stat label="Capacity" value={c.weekly_capacity || '-'} />
                  <Stat label="Overdue" value={c.overdue_tasks} accent={c.overdue_tasks > 0 ? 'red' : undefined} icon={<AlertTriangle size={11} />} />
                  <Stat label="Done (7d)" value={c.completed_last_7d} accent="green" icon={<Check size={11} />} />
                </div>
              </>
            )}
          </Card>

          <Card className="space-y-3">
            <h3 className="font-medium text-muted-strong text-sm inline-flex items-center gap-2">
              <Mail size={14} className="text-accent-400" /> Invite & access
            </h3>
            <div className="text-[11px] text-muted space-y-1">
              <div>Invited: {u.invited_at ? fmt(u.invited_at) : '—'}</div>
              <div>Expires: {u.invite_expires_at ? fmt(u.invite_expires_at) : '—'}</div>
              <div>Last seen: {u.last_seen_at ? fmt(u.last_seen_at) : 'never'}</div>
            </div>
            <Button size="sm" variant="secondary" icon={<RefreshCw size={12} />}
              onClick={() => reissue.mutate()} loading={reissue.isPending} className="w-full">
              Regenerate magic link
            </Button>
            {invite && (
              <div className="rounded-lg border border-accent-400/40 bg-accent-600/10 p-2 space-y-1.5">
                <div className="text-[10px] text-accent-400">Valid {invite.expires_in_days} days</div>
                <div className="flex gap-1.5">
                  <input readOnly value={invite.invite_url}
                    onClick={(e) => e.currentTarget.select()}
                    className="flex-1 h-7 rounded border border-slate-800 bg-bg px-1.5 text-[10px] text-muted-strong font-mono" />
                  <Button size="sm" icon={<Copy size={11} />} onClick={copyInvite}>Copy</Button>
                </div>
              </div>
            )}
          </Card>

          <Button variant="ghost" onClick={() => navigate('/admin/users')} className="w-full">
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}

function Toggle({ label, checked, onChange, hint }: { label: string; checked: boolean; onChange: (b: boolean) => void; hint?: string }) {
  return (
    <label className="flex items-start gap-3 text-sm cursor-pointer">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)}
        className="mt-1 accent-accent-400 cursor-pointer" />
      <div>
        <div className="text-muted-strong">{label}</div>
        {hint && <div className="text-[11px] text-muted">{hint}</div>}
      </div>
    </label>
  );
}

function Stat({ label, value, accent, icon }: { label: string; value: number | string; accent?: 'red' | 'green'; icon?: React.ReactNode }) {
  const color = accent === 'red' ? 'text-red-400' : accent === 'green' ? 'text-emerald-400' : 'text-muted-strong';
  return (
    <div className="rounded-lg border border-slate-800 p-2 text-center">
      <div className={cn('text-lg font-semibold inline-flex items-center gap-1', color)}>{icon}{value}</div>
      <div className="text-[10px] text-muted uppercase tracking-wider">{label}</div>
    </div>
  );
}

function fmt(iso: string) {
  return new Date(iso).toLocaleDateString('en-AE', { dateStyle: 'medium', timeZone: 'Asia/Dubai' });
}
